import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ErpProvider } from "@/context/ErpContext";
import { Toaster } from "@/components/ui/sonner";
import AppLayout from "@/components/layout/AppLayout";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import RegisterPage from "@/pages/RegisterPage";
import DashboardPage from "@/pages/DashboardPage";
import MyTasksPage from "@/pages/MyTasksPage";
import FixedDepositsPage from "@/pages/FixedDepositsPage";
import LoansPage from "@/pages/LoansPage";
import TransactionsPage from "@/pages/TransactionsPage";
import FxPage from "@/pages/FxPage";
import BankAccountsPage from "@/pages/BankAccountsPage";
import ReconciliationPage from "@/pages/ReconciliationPage";
import ApprovalsPage from "@/pages/ApprovalsPage";
import BankGuaranteePage from "@/pages/BankGuaranteePage";
import LetterOfCreditPage from "@/pages/LetterOfCreditPage";
import AutomationsPage from "@/pages/AutomationsPage";
import AgentSetupPage from "@/pages/admin/AgentSetupPage";
import UsersAccessPage from "@/pages/admin/UsersAccessPage";
import LlmSettingsPage from "@/pages/admin/LlmSettingsPage";
import VakuaiDataExplorerPage from "@/pages/admin/VakuaiDataExplorerPage";
import P2PCompliancePage from "@/pages/p2p/P2PCompliancePage";
import EpmPage from "@/pages/epm/EpmPage";
import EpbcsPage from "@/pages/epbcs/EpbcsPage";
import ReportingPage from "@/pages/reporting/ReportingPage";
import CommodityPage from "@/pages/commodity/CommodityPage";
import AlmDashboardPage from "@/pages/alm/AlmDashboardPage";
import AssetsMapPage from "@/pages/alm/AssetsMapPage";
import { FinanceBssPage, RevenueAssurancePage, SpectrumPage, SlicingPage } from "@/pages/telecom/TelecomPages";
import ProductMasterPage from "@/pages/alm/ProductMasterPage";
import SiteMasterPage from "@/pages/alm/SiteMasterPage";
import LocationMasterPage from "@/pages/alm/LocationMasterPage";
import PlanToProcurePage from "@/pages/alm/PlanToProcurePage";
import ReceivingProcessPage from "@/pages/alm/ReceivingProcessPage";
import AssetTrackingPage from "@/pages/alm/AssetTrackingPage";
import MovementOfItemsPage from "@/pages/alm/MovementOfItemsPage";
import AssetCapitalizationPage from "@/pages/alm/AssetCapitalizationPage";
import AssetMaintenancePage from "@/pages/alm/AssetMaintenancePage";
import AssetRetirementPage from "@/pages/alm/AssetRetirementPage";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import HomePage from "@/pages/marketing/HomePage";
import ProductsPage from "@/pages/marketing/ProductsPage";
import UseCasesPage from "@/pages/marketing/UseCasesPage";
import ProductTourPage from "@/pages/marketing/ProductTourPage";
import PartnersPage from "@/pages/marketing/PartnersPage";
import SolutionsPage from "@/pages/marketing/SolutionsPage";
import IndustryPage from "@/pages/marketing/IndustryPage";
import InsightsPage from "@/pages/marketing/InsightsPage";

function Marketing({ children }) {
  return <MarketingLayout>{children}</MarketingLayout>;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center text-xs tracking-[0.2em] uppercase font-bold text-gray-500">Loading…</div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return <AppLayout>{children}</AppLayout>;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <ErpProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Marketing><HomePage /></Marketing>} />
            <Route path="/products" element={<Marketing><ProductsPage /></Marketing>} />
            <Route path="/use-cases" element={<Marketing><UseCasesPage /></Marketing>} />
            <Route path="/solutions" element={<Marketing><SolutionsPage /></Marketing>} />
            <Route path="/solutions/:industryId" element={<Marketing><IndustryPage /></Marketing>} />
            <Route path="/insights" element={<Marketing><InsightsPage /></Marketing>} />
            <Route path="/tour" element={<ProductTourPage />} />
            <Route path="/partners" element={<Marketing><PartnersPage /></Marketing>} />
            <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
            <Route path="/forgot-password" element={<PublicOnly><ForgotPasswordPage /></PublicOnly>} />
            <Route path="/reset-password" element={<PublicOnly><ResetPasswordPage /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><RegisterPage /></PublicOnly>} />
            <Route path="/dashboard" element={<Protected><DashboardPage /></Protected>} />
            <Route path="/my-tasks" element={<Protected><MyTasksPage /></Protected>} />
            <Route path="/fixed-deposits" element={<Protected><FixedDepositsPage /></Protected>} />
            <Route path="/loans" element={<Protected><LoansPage /></Protected>} />
            <Route path="/transactions" element={<Protected><TransactionsPage /></Protected>} />
            <Route path="/fx" element={<Protected><FxPage /></Protected>} />
            <Route path="/bank-accounts" element={<Protected><BankAccountsPage /></Protected>} />
            <Route path="/reconciliation" element={<Protected><ReconciliationPage /></Protected>} />
            <Route path="/approvals" element={<Protected><ApprovalsPage /></Protected>} />
            <Route path="/bank-guarantees" element={<Protected><BankGuaranteePage /></Protected>} />
            <Route path="/letters-of-credit" element={<Protected><LetterOfCreditPage /></Protected>} />
            <Route path="/automations" element={<Protected><AutomationsPage /></Protected>} />
            <Route path="/admin/agent-setup" element={<Protected><AgentSetupPage /></Protected>} />
            <Route path="/admin/users" element={<Protected><UsersAccessPage /></Protected>} />
            <Route path="/admin/database" element={<Protected><VakuaiDataExplorerPage /></Protected>} />
            <Route path="/p2p-compliance" element={<Protected><P2PCompliancePage /></Protected>} />
            <Route path="/epm" element={<Protected><EpmPage /></Protected>} />
            <Route path="/epm/consolidation" element={<Protected><EpmPage /></Protected>} />
            <Route path="/planning" element={<Protected><EpbcsPage /></Protected>} />
            <Route path="/planning/:section" element={<Protected><EpbcsPage /></Protected>} />
            <Route path="/planning/:section/:sub" element={<Protected><EpbcsPage /></Protected>} />
            <Route path="/commodity" element={<Protected><CommodityPage /></Protected>} />
            <Route path="/commodity/:section" element={<Protected><CommodityPage /></Protected>} />
            <Route path="/reporting" element={<Protected><ReportingPage /></Protected>} />
            <Route path="/reporting/:section" element={<Protected><ReportingPage /></Protected>} />
            <Route path="/admin/llm-settings" element={<Protected><LlmSettingsPage /></Protected>} />
            <Route path="/alm/dashboard" element={<Protected><AlmDashboardPage /></Protected>} />
            <Route path="/alm/map" element={<Protected><AssetsMapPage /></Protected>} />
            <Route path="/telecom/finance-bss" element={<Protected><FinanceBssPage /></Protected>} />
            <Route path="/telecom/revenue-assurance" element={<Protected><RevenueAssurancePage /></Protected>} />
            <Route path="/telecom/spectrum" element={<Protected><SpectrumPage /></Protected>} />
            <Route path="/telecom/slicing" element={<Protected><SlicingPage /></Protected>} />
            <Route path="/alm/products" element={<Protected><ProductMasterPage /></Protected>} />
            <Route path="/alm/sites" element={<Protected><SiteMasterPage /></Protected>} />
            <Route path="/alm/locations" element={<Protected><LocationMasterPage /></Protected>} />
            <Route path="/alm/procurement-plans" element={<Protected><PlanToProcurePage /></Protected>} />
            <Route path="/alm/receipts" element={<Protected><ReceivingProcessPage /></Protected>} />
            <Route path="/alm/assets" element={<Protected><AssetTrackingPage /></Protected>} />
            <Route path="/alm/movements" element={<Protected><MovementOfItemsPage /></Protected>} />
            <Route path="/alm/capitalizations" element={<Protected><AssetCapitalizationPage /></Protected>} />
            <Route path="/alm/maintenance" element={<Protected><AssetMaintenancePage /></Protected>} />
            <Route path="/alm/retirements" element={<Protected><AssetRetirementPage /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster richColors position="top-right" />
        </BrowserRouter>
      </ErpProvider>
    </AuthProvider>
  );
}
